import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, CircularProgress, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { WhatsApp, Sms } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { signUpUser, verifyEmailCode, getUser } from '../../containers/SignupPage/sagas/saga';
import { showNotification } from '../snackbar/saga';
import { _registerHost } from '../../utilities/authWizard';
import { requestInboundCode, pollInboundStatus, fetchInboundInfo } from './inboundSaga';

const POLL_INTERVAL_MS = 3000;

/**
 * Single auth modal. Replaces SignupPage + LoginPage.
 *
 * State machine:
 *   identifier      → Email / Phone tab toggle
 *     Email   → "Send code"  → 'code'
 *     Phone   → "Get a code" → 'phone-waiting'
 *   code            → 6-digit field → verify
 *   phone-waiting   → big nonce + helpline + polling — no user input
 *
 * No name capture: a fresh signup finishes immediately on verify; the user
 * sets up their profile later via the profile flow.
 */
function AuthWizard({ open, initialEmail = '', onClose, onSuccess }) {
  const intl = useIntl();
  const [stage, setStage] = useState('identifier'); // 'identifier' | 'code' | 'phone-waiting'
  const [method, setMethod] = useState('email'); // 'email' | 'phone'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  // Inbound phone flow state.
  const [nonce, setNonce] = useState('');
  const [helplineNumber, setHelplineNumber] = useState('');
  const [verifiedCount, setVerifiedCount] = useState(0); // social proof
  const [stallHint, setStallHint] = useState(false); // shown after 60s of polling

  // Reset every time the modal closes — never leak state across opens.
  useEffect(() => {
    if (!open) {
      setStage('identifier');
      setMethod('email');
      setEmail(initialEmail);
      setCode('');
      setBusy(false);
      setNonce('');
      setHelplineNumber('');
      setStallHint(false);
    }
  }, [open, initialEmail]);

  // Poll /auth/phone/inbound/status while we're in 'phone-waiting'.
  // No timeout per design — user can wait forever. After 60s of polling
  // without resolution, show a "still waiting" hint.
  const pollTimer = useRef(null);
  const stallTimer = useRef(null);
  useEffect(() => {
    if (stage !== 'phone-waiting' || !nonce || !open) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await pollInboundStatus({ nonce });
        if (cancelled) return;
        if (res.status === 'verified') {
          // Server has set new cookies. Re-fetch the user so Redux picks
          // up the new identity.
          await getUser();
          finish(res.user);
          return;
        }
      } catch {
        // Soft-fail — next tick will retry. A 403 (someone else's nonce)
        // is the one case we should bail on; we leave that as an exercise.
      }
      pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
    stallTimer.current = setTimeout(() => setStallHint(true), 60_000);
    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
      if (stallTimer.current) clearTimeout(stallTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, nonce, open]);

  const requestCode = async () => {
    if (!email.trim()) {
      showNotification({
        isError: true,
        message: intl.formatMessage({ id: 'auth.error.emailRequired', defaultMessage: 'Email is required' }),
      });
      return;
    }
    setBusy(true);
    try {
      await signUpUser({ email: email.trim(), name: '', phone: '' });
      setStage('code');
    } finally {
      setBusy(false);
    }
  };

  const startPhoneFlow = async () => {
    setBusy(true);
    try {
      // force:true bypasses the in-memory cache so the wizard always sees
      // the live helpline number — env changes via `up -d --force-recreate
      // api` don't take effect until the SPA tab is reloaded otherwise.
      const [reqRes, info] = await Promise.all([
        requestInboundCode(),
        fetchInboundInfo({ force: true }).catch(() => null),
      ]);
      setNonce(reqRes.nonce);
      setHelplineNumber(reqRes.helplineNumber || info?.helplineNumber || '');
      setVerifiedCount(info?.verifiedCount || 0);
      setStage('phone-waiting');
    } catch (e) {
      showNotification({
        isError: true,
        message:
          e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({
            id: 'auth.error.phoneStartFailed',
            defaultMessage: 'Could not start phone verification',
          }),
      });
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) {
      showNotification({
        isError: true,
        message: intl.formatMessage({ id: 'auth.error.codeRequired', defaultMessage: 'Enter the 6-digit code' }),
      });
      return;
    }
    setBusy(true);
    try {
      const user = await verifyEmailCode({
        email: email.trim(),
        code: code.trim(),
        name: '',
      });
      if (!user) return;
      finish(user);
    } finally {
      setBusy(false);
    }
  };

  const finish = (user) => {
    onSuccess?.(user);
    onClose();
  };

  const cancelLabel = intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' });
  const emailLabel = intl.formatMessage({ id: 'auth.email.label', defaultMessage: 'Email' });

  return (
    <Modal open={open} onClose={onClose} className="flex items-center justify-center">
      <div className="bg-white dark:bg-[#1c1d22] dark:text-gray-100 rounded-lg p-6 mx-4 w-full max-w-[420px] outline-none">
        {stage === 'identifier' && (
          <>
            <h2 className="text-[22px] font-bold mb-2">
              <FormattedMessage id="auth.modal.title.signIn" defaultMessage="Sign in to dresez" />
            </h2>
            <p className="text-[14px] text-gray-600 mb-3">
              <FormattedMessage
                id="auth.modal.intro"
                defaultMessage="New here? Same flow — your account is created on verify."
              />
            </p>
            <ToggleButtonGroup
              value={method}
              exclusive
              onChange={(_, v) => v && setMethod(v)}
              size="small"
              className="mb-3"
              fullWidth
            >
              <ToggleButton value="email">
                <FormattedMessage id="auth.tab.email" defaultMessage="Email" />
              </ToggleButton>
              <ToggleButton value="phone">
                <FormattedMessage id="auth.tab.phone" defaultMessage="Phone" />
              </ToggleButton>
            </ToggleButtonGroup>

            {method === 'email' && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label={emailLabel}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="dense"
                  autoFocus
                />
                <div className="flex flex-col gap-2 mt-4">
                  <Button variant="contained" onClick={requestCode} disabled={busy}>
                    {busy ? (
                      <CircularProgress size={20} />
                    ) : (
                      <FormattedMessage id="auth.button.sendCode" defaultMessage="Send code" />
                    )}
                  </Button>
                  <Button onClick={onClose} color="inherit">
                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                  </Button>
                </div>
              </>
            )}

            {method === 'phone' && (
              <>
                <p className="text-[13px] text-gray-600 mb-3">
                  <FormattedMessage
                    id="auth.phone.intro"
                    defaultMessage="We’ll show you a one-time code. Send it via SMS or WhatsApp to dresez and our team verifies it manually — usually a few seconds to a couple of minutes. No app install, no paid SMS provider."
                  />
                </p>
                <div className="flex flex-col gap-2 mt-2">
                  <Button variant="contained" onClick={startPhoneFlow} disabled={busy}>
                    {busy ? (
                      <CircularProgress size={20} />
                    ) : (
                      <FormattedMessage id="auth.button.getCode" defaultMessage="Get a code" />
                    )}
                  </Button>
                  <Button onClick={onClose} color="inherit">
                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {stage === 'code' && (
          <>
            <h2 className="text-[22px] font-bold mb-2">
              <FormattedMessage id="auth.code.title" defaultMessage="Enter the code" />
            </h2>
            <p className="text-[14px] text-gray-600 mb-4">
              <FormattedMessage
                id="auth.code.sentTo"
                defaultMessage="Sent to {emailNode}. Check spam if it’s not in the inbox."
                values={{ emailNode: <b>{email}</b> }}
              />
            </p>
            <TextField
              fullWidth
              size="small"
              label={intl.formatMessage({ id: 'auth.code.label', defaultMessage: '6-digit code' })}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              margin="dense"
              autoFocus
            />
            <div className="flex flex-col gap-2 mt-4">
              <Button variant="contained" onClick={verify} disabled={busy}>
                {busy ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormattedMessage id="auth.button.verify" defaultMessage="Verify" />
                )}
              </Button>
              <Button onClick={() => setStage('identifier')} color="inherit">
                <FormattedMessage id="auth.button.useDifferentEmail" defaultMessage="Use a different email" />
              </Button>
            </div>
          </>
        )}

        {stage === 'phone-waiting' && (
          <>
            <h2 className="text-[22px] font-bold mb-2">
              <FormattedMessage id="auth.phone.waiting.title" defaultMessage="Send your code to dresez" />
            </h2>
            <p className="text-[13px] text-gray-600 mb-3">
              <FormattedMessage
                id="auth.phone.waiting.intro"
                defaultMessage="Tap a button below to message us your code — it’s filled in for you, just hit send. Our team verifies it manually, usually within a few seconds to a couple of minutes. No app install, no paid SMS."
              />
            </p>
            <div className="my-3 bg-gray-50 dark:bg-[#23252b] border border-gray-200 dark:border-[#2a2c30] rounded p-4 text-center">
              <div className="text-[12px] text-gray-500 dark:text-gray-400 mb-1">
                <FormattedMessage id="auth.phone.yourCode" defaultMessage="Your code" />
              </div>
              <div className="text-[36px] font-mono font-bold tracking-widest">{nonce}</div>
            </div>
            {helplineNumber ? (
              <div className="flex flex-col gap-2 mb-1">
                <Button
                  component="a"
                  variant="contained"
                  color="success"
                  startIcon={<WhatsApp />}
                  href={`https://wa.me/${helplineNumber.replace(/[^0-9+]/g, '').replace(/^\+/, '')}?text=${encodeURIComponent(nonce)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="auth.phone.sendWhatsApp" defaultMessage="Send code on WhatsApp" />
                </Button>
                <Button
                  component="a"
                  variant="contained"
                  startIcon={<Sms />}
                  href={`sms:${helplineNumber.replace(/[^0-9+]/g, '')}?body=${encodeURIComponent(nonce)}`}
                >
                  <FormattedMessage id="auth.phone.sendSms" defaultMessage="Send code by SMS" />
                </Button>
              </div>
            ) : (
              <p className="text-[13px] text-gray-500 mb-2 text-center">
                <FormattedMessage
                  id="auth.phone.sendOptionsError"
                  defaultMessage="Couldn’t load the send options just now — please try again in a moment."
                />
              </p>
            )}
            <div className="flex items-center gap-2 text-[13px] text-gray-500 mt-2">
              <CircularProgress size={14} />
              <span>
                <FormattedMessage id="auth.phone.waiting" defaultMessage="Waiting for verification…" />
              </span>
            </div>
            {verifiedCount >= 10 && (
              <p className="text-[12px] text-gray-500 mt-2">
                <FormattedMessage
                  id="auth.phone.joinedCount"
                  defaultMessage="Joined this way by {countNode} people so far."
                  values={{ countNode: <b>{verifiedCount.toLocaleString()}</b> }}
                />
              </p>
            )}
            {stallHint && (
              <p className="text-[13px] text-gray-500 mt-2">
                <FormattedMessage
                  id="auth.phone.stallHint"
                  defaultMessage="Still waiting. Double-check you sent {nonceNode} to the dresez helpline. Verification usually takes a few seconds to a couple of minutes — we don’t time out, so feel free to leave this open."
                  values={{ nonceNode: <b>{nonce}</b> }}
                />
              </p>
            )}
            <div className="flex flex-col gap-2 mt-4">
              <Button onClick={() => setStage('identifier')} color="inherit">
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
            </div>
          </>
        )}

      </div>
    </Modal>
  );
}

/**
 * Mount this near the App root. It listens for openAuthWizard() calls and
 * pops the modal. Only one instance should be mounted in the tree.
 */
export function AuthWizardHost() {
  const [open, setOpen] = useState(false);
  const [initialEmail, setInitialEmail] = useState('');

  useEffect(() => {
    return _registerHost(({ email } = {}) => {
      setInitialEmail(email || '');
      setOpen(true);
    });
  }, []);

  return (
    <AuthWizard
      open={open}
      initialEmail={initialEmail}
      onClose={() => setOpen(false)}
      onSuccess={() => setOpen(false)}
    />
  );
}
